import { useNavigate } from "react-router-dom";
import { Phone, MessageCircle, ChevronRight, Clock, MapPin } from "lucide-react";
import { StatusBadge, fmtDate } from "@/components/common";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";

export function LeadCard({ lead, statuses, onChanged, selectable = false, selected = false, onToggleSelect }) {
  const navigate = useNavigate();
  const sm = statuses?.find((s) => s.slug === lead.current_status);

  const call = async (e) => {
    e.stopPropagation();
    try { await api.post(`/leads/${lead.id}/call`); } catch { /* noop */ }
    window.location.href = `tel:${lead.mobile}`;
    onChanged && onChanged();
  };
  const wa = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/leads/${lead.id}/whatsapp`, {});
      if (data.whatsapp_url) window.open(data.whatsapp_url, "_blank");
    } catch { /* noop */ }
    onChanged && onChanged();
  };

  const onCardClick = () => selectable ? onToggleSelect?.(lead.id) : navigate(`/app/leads/${lead.id}`);

  return (
    <div data-testid={`lead-card-${lead.id}`} onClick={onCardClick}
      className={`rounded-2xl border bg-slate-900/60 p-4 active:scale-[0.99] transition-transform cursor-pointer ${selected ? "border-primary ring-2 ring-primary/50" : "border-slate-800"}`}>
      <div className="flex items-start justify-between gap-2">
        {selectable && (
          <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
            <Checkbox checked={selected} onCheckedChange={() => onToggleSelect?.(lead.id)} data-testid={`select-lead-${lead.id}`} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[15px] truncate">{lead.name}</span>
            <span className="font-mono text-[11px] text-slate-500">#{lead.lead_number}</span>
          </div>
          <div className="text-xs text-slate-400 font-mono mt-0.5">{lead.mobile}</div>
          <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-[11px] text-slate-500 mt-1.5">
            {lead.product_interest && <span>{lead.product_interest}</span>}
            {lead.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{lead.city}</span>}
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(lead.created_at)}</span>
          </div>
        </div>
        {!selectable && <ChevronRight className="h-5 w-5 text-slate-600 shrink-0" />}
      </div>

      <div className="flex items-center gap-2 mt-2.5">
        {sm && <StatusBadge status={sm.name} colorToken={sm.color_token} />}
        {lead.source && <span className="text-[10px] text-slate-500 border border-slate-700 rounded-full px-2 py-0.5">{lead.source}</span>}
      </div>
      {lead.latest_remark && (
        <div data-testid={`lead-remark-${lead.id}`} className="mt-2 text-[12px] text-slate-400 bg-slate-800/40 rounded-lg px-2.5 py-1.5 line-clamp-2">
          <span className="text-slate-500">Remark:</span> {lead.latest_remark}
        </div>
      )}

      {!selectable && (
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button onClick={call} data-testid={`quick-call-${lead.id}`}
            className="flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground py-2.5 text-sm font-bold">
            <Phone className="h-4 w-4" /> Call Now
          </button>
          <button onClick={wa} data-testid={`quick-whatsapp-${lead.id}`}
            className="flex items-center justify-center gap-2 rounded-xl border border-emerald-500/40 text-emerald-400 py-2.5 text-sm font-bold">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </button>
        </div>
      )}
    </div>
  );
}

export function LeadCardSkeleton() {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 animate-pulse">
      <div className="h-4 w-32 bg-slate-800 rounded" />
      <div className="h-3 w-24 bg-slate-800 rounded mt-2" />
      <div className="flex gap-2 mt-3"><div className="h-5 w-16 bg-slate-800 rounded-full" /><div className="h-5 w-20 bg-slate-800 rounded-full" /></div>
      <div className="grid grid-cols-2 gap-2 mt-3"><div className="h-10 bg-slate-800 rounded-xl" /><div className="h-10 bg-slate-800 rounded-xl" /></div>
    </div>
  );
}

export function EmptyState({ title, subtitle, icon: Icon }) {
  return (
    <div className="text-center py-16 px-6" data-testid="empty-state">
      {Icon && <div className="mx-auto h-14 w-14 rounded-2xl bg-slate-800/60 flex items-center justify-center mb-4"><Icon className="h-6 w-6 text-slate-500" /></div>}
      <p className="font-heading font-semibold text-slate-200">{title}</p>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
}
